import {
  Navigate,
  Outlet,
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
} from '@tanstack/react-router'

import type { ScreenKey } from './nav'
import { NAV_REGISTRY } from './nav'
import { Shell } from './shell'
import { BugReportLauncher } from './bug-report-launcher'
import { AlertScreen } from '../screens/alert'
import { TodayScreen } from '../screens/today'
import { AuthScreen } from '../screens/auth'
import { CodeScreen } from '../screens/code'
import { DevelopmentTaskScreen } from '../screens/development-task'
import { PublicDevelopmentTaskLinkScreen } from '../screens/development-task/public-link'
import { RemoteAIJoinScreen } from '../screens/remote-ai-join'
import { HostsScreen } from '../screens/hosts'
import { MemoArchiveScreen, MemoScreen } from '../screens/memo'
import { LuluRecordsScreen } from '../screens/memo/candidates'
import { ModelsScreen } from '../screens/models'
import { AgentsScreen } from '../screens/agents'
import { LuluScreen } from '../screens/lulu'
import { SkillsScreen } from '../screens/skills'
import { SystemModelsScreen } from '../screens/system-models'
import { OnboardScreen } from '../screens/onboard'
import { AccountScreen } from '../screens/account'
import { BridgeScreen } from '../screens/bridge'
import { ProjectListScreen } from '../screens/project/list'
import { NewProjectScreen } from '../screens/project/new'
import { ProjectShell } from '../screens/project/shell'
import { ProjectOverview } from '../screens/project/modules/overview'
import { ProjectConversations } from '../screens/project/modules/conversations'
import { ProjectTasks } from '../screens/project/modules/tasks'
import { ProjectEnvironments } from '../screens/project/modules/environments'
import { ProjectResources } from '../screens/project/modules/resources'
import { ProjectReleases } from '../screens/project/modules/releases'
import { ProjectMemos } from '../screens/project/modules/memos'
import { ProjectImportCreateScreen } from '../screens/project-import'
import { ProjectImportDetailScreen } from '../screens/project-import/detail'
import { ServerScreen } from '../screens/server'
import { DutyScreen } from '../screens/duty'
import { TeamScreen } from '../screens/team'
import { InviteRedeemScreen } from '../screens/team/redeem'
import { Soon } from '../screens/soon'
import { SecScreen } from '../screens/sec'
import { TimelineScreen } from '../screens/timeline'
import { ActivityScreen, ProjectActivityScreen } from '../screens/activity'
import { VendorScreen } from '../screens/vendor'
import { BugReportsScreen } from '../screens/bug-reports'

/**
 * 路由。用代码式定义而不是文件式：17 屏共享同一层外壳
 * （232px 侧边栏 + 四组导航），代码式看得见这层嵌套关系。
 *
 * 两层结构，不是一层：
 *   root  只有一个 Outlet
 *   shell 无路径的布局层，侧边栏 + 主内容区。**除登录页以外的屏都在它下面**
 *
 * 登录页挂在 root 而不是 shell 下面，因为**还没登录的人不该看到侧边栏**。
 * 设计稿把它放在导航的「设置」组里（那是可点着看的原型），真产品里点过去是整屏切换。
 * 这是我做的判断，不是设计稿写死的。
 *
 * 做完的屏**逐条写出路径**，没做的才由 NAV_GROUPS 批量生成占位。
 * 这不是啰嗦：路径写成字面量，TanStack Router 才能在编译期检查 `<Link to>` 与参数名——
 * 批量生成出来的路由，路由表里只剩 `string`，写错一个路径要到点上去才发现。
 * 每做完一屏，就把它从下面那批占位里挪上来。
 */
const rootRoute = createRootRoute({
  component: () => <><BugReportLauncher /><Outlet /></>,
})

const shellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'shell',
  component: Shell,
})

// ---------------------------------------------------------------- 做完的屏

// `/` 不再是 Dashboard——登录后的默认入口是 Lulu（产品合同：NPC 不提供
// 全局 Dashboard 作为默认入口）。保留 `/` 这个 URL 作为「回到家」，
// 用户书签和旧链接不断。
//
// 2026-08-25 起指向 `/lulu` 而不是 `/chat`：设计交接包把「根路由和 /lulu
// 都进入 Lulu」写成了冻结合同。
//
// 2026-08-26 把最后一个没跟上的入口也收齐了：顶栏那条「Lulu」、品牌 logo、
// 登录成功落点原来都还送到 `/chat`，而生产上 `/chat` 主区是空白的。
// 现在 `/`、`/lulu`、`/chat` 三条都规范化到同一个可见的工作台。
// R1（PRD FR-1）：登录后默认落在今天屏——用户打开只看这一屏就知道全部。
// 「今天」是北极星首屏；Lulu 工作台仍在顶栏第一项。
const dashRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/',
  component: () => <Navigate to="/today" replace />,
})

const todayRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/today',
  component: TodayScreen,
})

const hostsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/hosts',
  component: HostsScreen,
})

/** 「加一台机器」是动作不是地方，所以它在 /hosts 下面，而不在导航里。 */
const onboardRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/hosts/new',
  component: OnboardScreen,
})

const serverRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/hosts/$hostId',
  component: ServerScreen,
})

/** 登录页：整屏，没有侧边栏。search 里可带 return_to（登录完回去的站内路径，见 auth/form）。 */
const authRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth',
  component: AuthScreen,
})

/** 单任务 bearer 就是登录证；此页刻意不挂需要账户会话的 Shell。 */
const publicDevelopmentTaskLinkRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/ai-task',
  component: PublicDevelopmentTaskLinkScreen,
})

/** 一次性 token 只在 URL fragment；激活后立即从地址栏移除。 */
const remoteAIJoinRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/remote-ai/join',
  component: RemoteAIJoinScreen,
})

/** 本机老板 AI 设备与远程 AI 共用一次性兑换实现，但使用独立路由与文案。 */
const localAIJoinRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/local-ai/join',
  component: RemoteAIJoinScreen,
})

/**
 * 「你自己」。**注册指纹的常驻入口**，侧边栏底部那一块点进来就是它。
 *
 * 不在导航的四组里：它不是一个「每天看的东西」，而是一个
 * **需要的时候一定找得到的地方**。
 */
const accountRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/account',
  component: AccountScreen,
})

const bridgeRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/bridge',
  component: BridgeScreen,
})

/**
 * 项目列表。**导航里「项目」那一条点进来就是它。**
 *
 * 它以前不存在：设计稿 17 屏里 `project` 是详情，没有列表屏，
 * 于是 `/projects` 一直落在下面那批批量生成的占位路由上——
 * **点进去是「这一屏还没做」，而做好的详情屏只能从总览的项目卡进去。**
 * 总览 404 的那几天两条路同时断着，arron 的原话是「项目都没有」。
 */
const projectListRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/projects',
  component: ProjectListScreen,
})

/** 新建项目。静态段必须写在 `$projectId` 前面，否则 `new` 会被当成项目 id。 */
const projectNewRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/projects/new',
  component: NewProjectScreen,
})

/**
 * 导入向导。必须是 `/projects/$projectId` 的兄弟路由，而且静态段
 * `/projects/import` 要写在参数段前面——不然 `import` 会被当成项目 id。
 */
const projectImportCreateRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/projects/import',
  component: ProjectImportCreateScreen,
})

const projectImportDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/projects/import/$importId',
  component: ProjectImportDetailScreen,
})

/** 项目详情布局。七个模块是子路由，不再靠 hash。 */
const projectRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/projects/$projectId',
  component: ProjectShell,
})

const projectOverviewRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/',
  component: ProjectOverview,
})

const projectConversationsRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/conversations',
  component: ProjectConversations,
})

const projectTasksRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/tasks',
  component: ProjectTasks,
})

const projectBugReportsRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/bug-reports',
  component: BugReportsScreen,
})

const projectEnvironmentsRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/environments',
  component: ProjectEnvironments,
})

const projectResourcesRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/resources',
  component: ProjectResources,
})

const projectReleasesRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/releases',
  component: ProjectReleases,
})

const projectMemosRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/memos',
  component: ProjectMemos,
})

const projectActivityRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/activity',
  component: ProjectActivityScreen,
})

/** 开发任务从项目页创建，但详情可被另一台电脑的 AI 安全地深链打开。 */
const developmentTaskRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/development-tasks/$taskId',
  component: DevelopmentTaskScreen,
})

/**
 * 「备忘录」2026-08 设计稿：`/memos`（杂项）+ `/memos/archived`（归档区）
 * + `/projects/:id/memos`（项目内，同一组件）。`/memo` 是确定性重定向——
 * 兼容旧链接，301 的 SPA 等价物就是 replace 跳转。
 * `/memos/lulu` 是「Lulu 建议记录」页签——用户定的：备忘录页只放他自己
 * 记的，Lulu 的候选单独一页（2026-09-09 从备忘录页顶上挪出来）。
 */
const memosRedirectRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/memo',
  beforeLoad: () => {
    throw redirect({ to: '/memos', replace: true })
  },
})

const memosRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/memos',
  component: MemoScreen,
})

const memosArchivedRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/memos/archived',
  component: MemoArchiveScreen,
})

const memosLuluRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/memos/lulu',
  component: LuluRecordsScreen,
})

/** 「出事的时候」。 */
const alertRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/alerts',
  component: AlertScreen,
})

/** 「一起管」。 */
const teamRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/team',
  component: TeamScreen,
})

const bugReportsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/bug-reports',
  component: BugReportsScreen,
})

/**
 * 邀请兑换页：链接落点，已登录自动入队（闭环 2026-09-05）。
 * token 从链接的查询参数来（组件里 loose 读取）；没登录先去 /auth 带
 * return_to 回本页。这里不挂 validateSearch——挂了会把全站 loose
 * setSearch 的联合类型搅浑（lulu 的 sidecar 就断过一次）。
 */
const teamInviteRedeemRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/team/invite',
  component: InviteRedeemScreen,
})

/** 「代码与发布」。 */
const codeRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/code',
  component: CodeScreen,
})

/** 「服务商与账号」。 */
const vendorRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/vendors',
  component: VendorScreen,
})

/** 模型库：连接和模型分层，密钥只写入服务端 Vault，浏览器永不回显。 */
const modelsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/models',
  component: ModelsScreen,
})

const systemModelsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/system-models',
  component: SystemModelsScreen,
})

/**
 * `/chat` 是**旧书签的兼容入口**，`replace` 到 `/lulu`。
 *
 * 2026-08-26 生产验收：点顶栏「Lulu」落在 `/chat`，主区完全空白。
 * 旧屏要的后端不在 main 上，而新工作台在 `/lulu` 是好的、0 console error。
 *
 * 所以这条路径交给新工作台，而不是把旧屏修到能看——**不维护第二套主屏**。
 * 两套屏各投影一套状态机（旧屏是单任务发布飞轮的六档阶段，新屏是设计包的
 * 12 态 + WorkPlan / WorkItem / SubAgentRun），留着两个入口只会让
 * 「现在是什么」有两个不一样的答案。
 *
 * 用 `replace` 不是 `push`：兼容跳转不该在浏览器历史里留一格，
 * 否则用户按返回会弹回 `/chat` 再被弹走，出不去。
 *
 * `screens/chat` 的代码 2026-09-02 删掉了——它在此之前就不再挂在任何路由上。
 * 删它靠的是「没人用它」这个判据（数过：零生产消费方），不是这条跳转。
 */
const chatRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/chat',
  component: () => <Navigate to="/lulu" replace />,
})

/**
 * Lulu 多 Agent 工作台（2026-08-25 设计交接包）。
 *
 * # `/lulu` 这条路径换了主人，这是刻意的
 *
 * 它原来是一条旧入口，`replace` 到 `/chat`——那是 Lulu 屏从 `/lulu` 搬到
 * `/chat` 那次留下的兼容跳转。**2026-08-25 的设计交接包把「根路由和 /lulu
 * 都进入 Lulu」写成了冻结合同**，所以这条路径收回来给新工作台，
 * 旧的那条跳转删掉了。
 *
 * 影响说清楚：书签在 `/lulu` 的人会落到新工作台而不是旧的 `/chat`。
 * 这正是合同要的效果，但它是一次**可见的行为变化**，不是重构。
 *
 * # 2026-08-26：不再有第二个入口
 *
 * 这段原来写着「旧屏本身没动，`/chat` 照常在」「两者是两个合同，
 * 不是同一屏的两个入口」。**那两句现在都不成立了**，留着会把人指反方向：
 * 照它去查会以为 `/chat` 还渲染着一屏，然后去找那屏为什么空。
 *
 * 实际是：生产上 `/chat` 主区完全空白（旧屏要的后端不在 main 上），
 * 而 `/lulu` 是好的。所以 `/chat` 改成 `replace` 到 `/lulu` 的兼容入口，
 * **旧屏 `src/screens/chat/` 先是只留源码、没有任何路由指向它，
 * 2026-09-02 整个删掉了**（`e2e/visual-qa/inventory.ts` 的 `retired`
 * 名单里那一行也随之去掉——一条指向不存在目录的豁免，看起来跟还在豁免着一样）。
 *
 * 为什么不是把新合同塞进旧屏：两套状态机共用一套组件，
 * 会让「现在是什么」有两个不一样的答案——`/chat` 投影的是单任务发布飞轮的
 * 六档阶段，这一条投影的是设计包的 12 态 + WorkPlan / WorkItem / SubAgentRun。
 *
 * `/lulu`、`/lulu/new`、`/lulu/c/$conversationId` 三条共用同一个组件，
 * 所以在会话之间跳转**不卸载聊天区**。
 * Sidecar 的开合走 `?sidecar=`，同样不换路由——换路由会把聊天列整个重建，
 * 滚动位置和草稿一起没，而验收 §D.22 逐条盯着这件事。
 */
const luluRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/lulu',
  component: LuluScreen,
})

/**
 * 新会话启动器。静态段写在参数段前面，否则 `new` 会被当成会话 id。
 *
 * 它与 `/lulu` 共用同一个组件：启动器是**主聊天区的一次切换**，
 * 不是覆盖层（SPEC-components §11）。用一条路由而不是一个 useState，
 * 是为了让「开始新会话」这一步能被后退键撤销。
 */
const luluNewRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/lulu/new',
  component: LuluScreen,
})

const luluConversationRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/lulu/c/$conversationId',
  component: LuluScreen,
})

/**
 * Agent 库（设计稿 SPEC-routes-api 的 `/agents`）。
 *
 * 与 `/duty` 是两件事：排班表读 `GET /api/v1/duty/skills`；这一屏读 `GET /agents`，说的是「你有哪几位 Agent」。
 *
 * 顶栏那一项「AI 团队」现在指 `/skills`——**没动它**：全局导航归 ZCode。
 * 设计稿 agents 那屏标的是 `nav:'squad'`，也就是这一项，
 * 要不要改指向由导航 owner 定。
 */
const agentsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/agents',
  component: AgentsScreen,
})

/** AI 团队：管理具名长期 Agent；临时子 Agent 只属于 Lulu 运行工作板。 */
const skillsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/skills',
  component: SkillsScreen,
})

/** 「他们干过什么」。总览那句「看时间线 →」指向它。 */
const timelineRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/timeline',
  component: TimelineScreen,
})

const activityRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/activity',
  component: ActivityScreen,
})

/**
 * 组件预览页。**只在开发时存在。**
 *
 * 它整页都是编出来的样例数据（「小食堂点单 · 后端 + 数据库」那种）。
 * 原来它是静态 import 的，于是**跟着产物一起上线，而且线上 /preview 真能打开**——
 * 一个上线之后还在显示假数据的页面，比一个 404 糟得多。
 *
 * 现在两道一起：路由只在 DEV 挂上，组件走动态 import。
 * `import.meta.env.DEV` 在生产构建里是常量 false，
 * 这个三元整条是死代码，`screens/preview.tsx` 一个字节都不会进产物。
 * （与 `mocks/dev.ts` 同一个手法，理由也一样。）
 */
const previewRoute = import.meta.env.DEV
  ? [
      createRoute({
        getParentRoute: () => shellRoute,
        path: '/preview',
        component: lazyRouteComponent(() => import('../screens/preview'), 'Preview'),
      }),
    ]
  : []

// ------------------------------------------------------------ 还没做的屏

/** 上面逐条写出来的那些。剩下的才批量生成占位。 */
const EXPLICIT = new Set<ScreenKey>([
  'dash', 'hosts', 'server', 'onboard', 'auth', 'timeline', 'alert', 'memo', 'team', 'vendor', 'code',
  'project', 'models', 'chat', 'skills', 'account', 'bridge', 'sec',
])

const secRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/security',
  component: SecScreen,
})

/**
 * 「数据备份」。**不在导航里**——它与「加一台机器」同类，是设置里的一块，
 * 入口在账户屏那张设置卡上。日常不用看，需要的时候找得到。
 */
const backupsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/backups',
  component: lazyRouteComponent(() => import('../screens/backups'), 'BackupsScreen'),
})

/**
 * 「排班与自动处置」。**不进顶栏七项**——入口在账户设置与告警屏。
 * /skills 仍是 Agent 库。
 */
const dutyRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/duty',
  component: DutyScreen,
})

const soonRoutes = NAV_REGISTRY
  .filter((item) => !EXPLICIT.has(item.key))
  .map((item) =>
    createRoute({
      getParentRoute: () => shellRoute,
      path: item.path,
      component: () => <Soon screen={item.key} />,
    }),
  )

/**
 * 路由树。导出是为了测试能拿同一棵树配一个内存 history 的 router——
 * 测试不该改这个模块里那个真的 router 的 history，那不是公开接口。
 */
export const routeTree = rootRoute.addChildren([
  authRoute,
  publicDevelopmentTaskLinkRoute,
  remoteAIJoinRoute,
  localAIJoinRoute,
  shellRoute.addChildren([
    dashRoute,
    hostsRoute,
    onboardRoute,
    serverRoute,
    accountRoute,
    bridgeRoute,
    projectListRoute,
    projectNewRoute,
    projectImportCreateRoute,
    projectImportDetailRoute,
    projectRoute.addChildren([
      projectOverviewRoute,
      projectConversationsRoute,
      projectBugReportsRoute,
      projectTasksRoute,
      projectEnvironmentsRoute,
      projectResourcesRoute,
      projectReleasesRoute,
      projectMemosRoute,
      projectActivityRoute,
    ]),
    developmentTaskRoute,
    alertRoute,
    memosRedirectRoute,
    memosRoute,
    memosArchivedRoute,
    memosLuluRoute,
    teamRoute,
    bugReportsRoute,
        teamInviteRedeemRoute,
    vendorRoute,
    modelsRoute,
    systemModelsRoute,
    todayRoute,
    chatRoute,
    luluRoute,
    luluNewRoute,
    luluConversationRoute,
    agentsRoute,
    skillsRoute,
    codeRoute,
    timelineRoute,
    activityRoute,
    ...previewRoute,
    secRoute,
    backupsRoute,
    dutyRoute,
    ...soonRoutes,
  ]),
])

export const router = createRouter({ routeTree })

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
